import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RazorpayPay } from "@/components/admission/RazorpayPay";
import { api, fmtINR } from "@/lib/api";
import { errText } from "@/lib/aadhaar";
import { TAGLINE } from "@/lib/offer";
import { useLang } from "@/context/LanguageContext";

const Row = ({ l, v, testid, strong }) => (
  <div className={`flex justify-between py-2 ${strong ? "border-t border-slate-200 mt-1 pt-3 text-lg font-black" : "text-sm"}`}>
    <span className={strong ? "text-[#0A192F]" : "text-slate-600"}>{l}</span><span className="font-bold text-[#0A192F] tabular-nums" data-testid={testid}>{v}</span>
  </div>
);

export default function SchoolBilling() {
  const { L } = useLang();
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/school-billing/${token}`).then((r) => setInv(r.data)).catch((e) => setErr(errText(e.response?.data?.detail, "Invoice not found"))), [token]);
  useEffect(() => { load(); }, [load]);

  const pay = async () => {
    setBusy(true);
    try { setOrder((await api.post(`/school-billing/${token}/pay`)).data); }
    catch (e) { toast.error(errText(e.response?.data?.detail, L("Could not start payment", "भुगतान शुरू नहीं हो सका"))); }
    finally { setBusy(false); }
  };
  const paid = useCallback(() => { setOrder(null); toast.success(L("Payment received. Thank you!", "भुगतान मिल गया। धन्यवाद!")); load(); }, [L, load]);

  return (
    <div className="min-h-[70vh] bg-[#E8F0FE] grid place-items-center px-5 py-14" data-testid="school-billing-page">
      <div className="bg-white border border-slate-200 rounded-3xl p-7 w-full max-w-lg shadow-[0_30px_80px_-30px_rgba(10,25,47,0.35)]">
        <div className="flex items-center gap-3"><Building2 className="w-6 h-6 text-[#FF2E63]" /><div><div className="text-xs font-bold text-[#FF2E63] italic">{L(...TAGLINE)}</div><h1 className="text-2xl font-extrabold text-[#0A192F]">{L("School billing", "स्कूल बिलिंग")}</h1></div></div>
        {err && <p className="mt-6 text-red-600" data-testid="school-billing-error">{err}</p>}
        {!inv && !err && <Loader2 className="w-6 h-6 animate-spin mx-auto mt-8" />}
        {inv && <>
          <p className="mt-4 font-bold text-[#0A192F]" data-testid="school-billing-name">{inv.school_name}</p>
          <p className="text-xs text-slate-500">{L("Invoice", "इनवॉइस")} {inv.invoice_no} · {inv.area === "rural" ? L("Rural", "ग्रामीण") : L("Municipal", "नगरपालिका")} · {inv.billing === "yearly" ? L("Yearly", "वार्षिक") : L("Monthly", "मासिक")} · {inv.roster_count} {L("students", "छात्र")}</p>
          <div className="mt-4">
            <Row l={L("Base fee", "मूल शुल्क")} v={fmtINR(inv.base_fee_inr)} testid="school-billing-base" />
            <Row l={L(`Onboarding discount (${inv.discount_pct}%)`, `ऑनबोर्डिंग छूट (${inv.discount_pct}%)`)} v={`− ${fmtINR(inv.discount_inr)}`} testid="school-billing-discount" />
            {inv.gst_inr > 0 && <Row l={L("Taxable value", "कर योग्य मूल्य")} v={fmtINR(inv.taxable_inr)} testid="school-billing-taxable" />}
            {inv.gst_inr > 0 && <Row l={`GST @ ${inv.gst_rate}%`} v={`+ ${fmtINR(inv.gst_inr)}`} testid="school-billing-gst" />}
            <Row l={L("Total due", "कुल देय")} v={fmtINR(inv.total_inr)} testid="school-billing-total" strong />
          </div>
          <p className="text-[11px] text-slate-500 mt-3" data-testid="school-billing-billed-by">{L("Billed by", "बिलकर्ता")} {inv.billed_by} {L("for RoboSpark", "(RoboSpark के लिए)")} · GSTIN {inv.gstin} · <a href={`${process.env.REACT_APP_BACKEND_URL}/api/school-billing/${token}/invoice.pdf`} className="underline font-semibold text-[#007A8A]" data-testid="school-billing-invoice-pdf">{inv.payment_status === "paid" ? L("Download tax invoice", "टैक्स इनवॉइस डाउनलोड") : L("Download proforma invoice", "प्रोफ़ॉर्मा इनवॉइस डाउनलोड")}</a></p>
          <p className="text-[11px] text-[#B42318] bg-[#FFF1F1] rounded-lg p-2.5 mt-2">{inv.kits_note}</p>
          {inv.payment_status === "paid" ? (
            <p className="mt-5 flex items-center gap-2 text-green-700 font-bold" data-testid="school-billing-paid"><CheckCircle2 className="w-5 h-5" />{L("Paid. Thank you!", "भुगतान हो गया। धन्यवाद!")}</p>
          ) : (
            <Button onClick={pay} disabled={busy} className="w-full mt-5 rs-cta h-12 rounded-full" data-testid="school-billing-pay-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `${L("Pay", "भुगतान करें")} ${fmtINR(inv.total_inr)} · UPI QR / card`}</Button>
          )}
        </>}
      </div>
      <RazorpayPay order={order} description={`RoboSpark school fee - ${inv?.school_name || ""}`} onPaid={paid} onClose={() => setOrder(null)} />
    </div>
  );
}
